import {MutualStaleReason, mutualStaleReason} from "features/betslip/lib/selection-guard";
import {BehaviorSubject} from "rxjs";
import {useSubjectSnapshot} from "features/core/subject.hooks";
import _ from "lodash";
import {combination, Permutation} from "js-combinatorics";
import {getDailyRaces} from "features/bulletin/bulletin.store";
import {postCoupon, toSubmitError} from "features/betslip/lib/submit";
import {modalServiceSubject} from "services/modal.service";
import {buildMutualBettingSubTypes, buildMutualBettingTypes} from "features/bulletin/bulletin.service";

export interface MutualSlipItem {
    hippodrome: any;
    bettingType: number;
    bettingSubType: number;
    column: number;
    run: number;
    horse: string;
}

export interface MutualSlipOptions {
    hippodrome: any;
    bettingType: any;
    bettingSubType: any;
    column: number;
    betSlipMultiply: any;
    maxAmount: any;
    usePointBalance: boolean;
    shareCoupon: boolean;
    complete: boolean;
    inProgress: boolean;
    errorMessage: string | null;
    // system/systemMaxMin eskiden getBetCount2'nin YAN ETKİSİYLE yazılıyordu (render sırasında
    // global obje mutasyonu). Artık her mutasyondan sonra syncSystemState() ile tazeleniyor.
    system: boolean;
    systemMaxMin: number[] | null;
}

const defaultMutualSlipOptions: MutualSlipOptions = {
    hippodrome: null,
    bettingType: null,
    bettingSubType: null,
    column: 1,
    betSlipMultiply: 1,
    maxAmount: 1,
    usePointBalance: false,
    shareCoupon: false,
    complete: false,
    inProgress: false,
    errorMessage: null,
    system: false,
    systemMaxMin: null,
};

// BehaviorSubject şart: useSyncExternalStore abone olur olmaz senkron bir "şu anki değer" istiyor.
const mutualBetslipSubject = new BehaviorSubject<MutualSlipItem[]>([]);
const mutualSlipOptionsSubject = new BehaviorSubject<MutualSlipOptions>(defaultMutualSlipOptions);

// Kupon satırı yalnızca hipodrom _id'si + koşu no tutuyor; canlı bülten koşusuna buradan iniliyor.
function resolveMutualRun(hippodromeId: any, runNo: any): any {
    return getDailyRaces().find((h: any) => h._id === hippodromeId)
        ?.runs?.find((r: any) => Number(r.no) === Number(runNo));
}

/** Satır neden oynanamıyor: koşu mu kapandı, at mı çekildi? Oynanabilirse null. */
export function staleReasonOf(item: MutualSlipItem): MutualStaleReason | null {
    return mutualStaleReason(resolveMutualRun(item.hippodrome, item.run), item.horse);
}

export function isMutualItemPlayable(item: MutualSlipItem): boolean {
    return staleReasonOf(item) === null;
}

class MutualBetslipService {
    private items: MutualSlipItem[] = [];
    private options: MutualSlipOptions = {...defaultMutualSlipOptions};

    getAll(): MutualSlipItem[] {
        return this.items;
    }

    getOptions(): MutualSlipOptions {
        return this.options;
    }

    getCurrentItems(): MutualSlipItem[] {
        const {hippodrome, bettingType, bettingSubType} = this.options;
        if (!hippodrome || !bettingType || !bettingSubType) {
            return [];
        }
        return this.items.filter((el) =>
            el.hippodrome === hippodrome._id &&
            el.bettingType === Number(bettingType.id) &&
            el.bettingSubType === Number(bettingSubType.id)
        );
    }

    reorder(oldIndex: number, newIndex: number) {
        const current = this.getCurrentItems();
        if (
            oldIndex === newIndex ||
            oldIndex < 0 || newIndex < 0 ||
            oldIndex >= current.length || newIndex >= current.length
        ) {
            return;
        }
        const positions = this.items.reduce<number[]>((acc, el, i) => {
            if (current.includes(el)) {
                acc.push(i);
            }
            return acc;
        }, []);
        const moved = [...current];
        moved.splice(newIndex, 0, moved.splice(oldIndex, 1)[0]);

        const next = [...this.items];
        positions.forEach((position, i) => {
            next[position] = moved[i];
        });
        this.items = next;
        this.update();
    }

    isAdded(column: any, run: any, horse: any): number | false {
        const {hippodrome, bettingType, bettingSubType} = this.options;
        if (!hippodrome || !bettingType || !bettingSubType) {
            return false;
        }
        const horseIndex = this.items.findIndex((el) =>
            el.hippodrome === hippodrome._id &&
            String(el.horse) === (horse !== "s" ? String(horse.no) : "s") &&
            el.column === column &&
            el.run === Number(run.no) &&
            el.bettingType === bettingType.id &&
            el.bettingSubType === bettingSubType?.id
        );
        return horseIndex === -1 ? false : horseIndex;
    }

    isAddedAll(column: any, run: any): boolean {
        const {hippodrome, bettingType, bettingSubType} = this.options;
        if (!hippodrome || !bettingType || !bettingSubType) {
            return false;
        }
        return this.items.filter((el) =>
            el.hippodrome === hippodrome._id &&
            el.column === column &&
            el.horse !== "s" &&
            el.run === Number(run?.no) &&
            el.bettingType === bettingType.id &&
            el.bettingSubType === bettingSubType.id
        ).length === run?.horses.filter((h: any) => h.run_status === true).length;
    }

    add(column: any, run: any, horse: any, action: string = "toggle", hippodrome: any = null) {
        if (this.options.bettingType == null) {
            modalServiceSubject.next({
                title: "Uyarı",
                content: "Lütfen bahis türü seçin.",
                timeout: 3000,
                confirm: {
                    cancel: {
                        label: "Tamam",
                    },
                },
            });
            return;
        }

        if (hippodrome != null) {
            this.setOptions({hippodrome: hippodrome});
        }

        if (this.options.hippodrome?.status !== true) {
            return;
        }

        this.options = {...this.options, errorMessage: null, column: column};

        if (this.options.inProgress === true) {
            modalServiceSubject.next({
                title: "Uyarı",
                content: "Bahisleriniz işleniyor. Lütfen bekleyiniz.",
                timeout: 3000,
                confirm: {
                    cancel: {
                        label: "Tamam",
                    },
                },
            });
            this.updateOptions();
            return;
        }

        const horseIndex = this.isAdded(column, run, horse);
        const isRemoval = horseIndex !== false && (action === "toggle" || action === "remove");

        // Koşmayacak ata (run_status false) YENİ bahis eklenmez; ama zaten seçilmiş bir at
        // sonradan çekilirse o satır KALDIRILABİLİR olmalı. Aksi hâlde kullanıcı geçersiz
        // seçime tıklayıp çıkaramıyor, kuponu tümden silmekten başka çıkışı kalmıyordu.
        if (!isRemoval && horse !== "s" && horse.run_status === false) {
            this.updateOptions();
            return;
        }

        if (horseIndex === false && (action === "toggle" || action === "add")) {
            this.items = [
                ...this.items,
                {
                    hippodrome: this.options.hippodrome._id,
                    bettingType: this.options.bettingType.id,
                    bettingSubType: this.options.bettingSubType.id,
                    column: column,
                    run: Number(run.no),
                    horse: horse !== "s" ? String(horse.no) : "s",
                },
            ];
            try {
                // @ts-ignore
                fbq("track", "AddToCart");
            } catch (e) {
                console.error(e);
            }
        } else if (horseIndex !== false && (action === "toggle" || action === "remove")) {
            this.items = this.items.filter((el, i) => i !== horseIndex);
        }

        this.update();
    }

    toggle(column: any, run: any, horse: any, hippodrome: any = null) {
        this.add(column, run, horse, "toggle", hippodrome);
    }

    remove(column: any, run: any, horse: any) {
        const {hippodrome, bettingType, bettingSubType} = this.options;
        if (!hippodrome || !bettingType || !bettingSubType) {
            return;
        }
        const horseIndex = this.items.findIndex((el) =>
            el.horse === String(horse.no) &&
            el.column === Number(column) &&
            el.run === Number(run.no) &&
            el.hippodrome === hippodrome._id &&
            el.bettingType === bettingType.id &&
            el.bettingSubType === bettingSubType.id
        );
        if (horseIndex !== -1) {
            this.items = this.items.filter((el, i) => i !== horseIndex);
            this.update();
        }
    }

    addAll(column: any, run: any, action: string = "add") {
        run.horses.forEach((horse: any) => {
            this.add(column, run, horse, action === "add" ? "add" : "remove");
        });
    }

    restoreItems(rows: MutualSlipItem[]) {
        const identity = (el: MutualSlipItem) => `${el.hippodrome}|${el.bettingType}|${el.bettingSubType}`;
        const replacedKeys = new Set(rows.map(identity));
        this.items = [...this.items.filter((el) => !replacedKeys.has(identity(el))), ...rows];
        this.update();
    }

    clear() {
        const current = this.getCurrentItems();
        this.items = this.items.filter((el) => !current.includes(el));
        this.options = {
            ...this.options,
            errorMessage: null,
            betSlipMultiply: 1,
            usePointBalance: false,
            shareCoupon: false,
            complete: false,
        };
        this.update();
    }

    setOptions(patch: Partial<MutualSlipOptions>) {
        this.options = {...this.options, ...patch};
        this.syncSystemState();
        this.updateOptions();
    }

    // Eski manuel memo (updateBetCount.status bayrağı + memoBetCount) kaldırıldı: bayrağı set
    // etmeyen bir yol (bülten tick'i) betCount'u bayat bırakıyordu. Artık her çağrıda yeniden hesap.
    getBetCount(): number {
        return this.computeBetCount().betCount;
    }

    getSystemState(): { system: boolean; systemMaxMin: number[] | null } {
        const {system, systemMaxMin} = this.computeBetCount();
        return {system, systemMaxMin};
    }

    getTotalAmount(): number {
        const {betCount, system} = this.computeBetCount();
        if (system) {
            return Number(this.options.maxAmount) || 0;
        }
        return Number(
            (
                Number(this.options.betSlipMultiply) *
                betCount *
                Number(this.options.bettingType?.price ?? 0)
            ).toFixed(2)
        );
    }

    // Misli seçicisinin üst sınırı: kupon tutarı bahis türünün maxCouponAmount'ını aşmasın.
    getMaxMultiply(): number {
        const betCount = this.getBetCount();
        const maxCouponAmount = Number(this.options.bettingType?.maxCouponAmount ?? 0);
        return betCount > 0 ? Math.floor(maxCouponAmount / betCount) : maxCouponAmount;
    }

    // Sunucuya giden ayak/kolon dizisi. Oynanamaz hale gelmiş bir satır varsa SESSİZCE ELEMEK yerine
    // senkron throw ediyoruz; yarım kupon oynatmaktansa gönderimin tamamı hata ile bitmeli.
    getCouponLegs(): any[] {
        const slipItem = _.orderBy(this.getCurrentItems(), ["column"]);
        const uniqueColumn = [...Array.from(new Set(slipItem.map((i: any) => i.column)))];
        const legs = [];
        for (const c of uniqueColumn) {
            const cItems = slipItem
                .filter((i: any) => i.column === c)
                .map((el: any) => {
                    if (!isMutualItemPlayable(el)) {
                        throw new Error("Kuponunuzdaki seçimlerden biri artık geçerli değil (koşu kapanmış ya da at koşudan çekilmiş olabilir). Lütfen işaretli seçimi kaldırıp tekrar deneyin.");
                    }
                    return el.horse !== "s" ? el.horse : "s";
                });
            legs.push(cItems);
        }
        return legs;
    }

    // Kupon panelinde ayak ayak gösterilen seçimler (boş ayaklar da yer tutsun diye tab tab dönülüyor).
    getLegsSelections(): any[] {
        const legs: any = [];
        const {bettingType, bettingSubType, hippodrome} = this.options;
        if (!hippodrome || !bettingType || !bettingSubType) {
            return legs;
        }

        const tabs: any = [];
        for (
            let i = Number(bettingSubType.id);
            i < Number(bettingSubType.id) + bettingType.column;
            i += 1
        ) {
            tabs.push(i);
        }

        let slipItem: any;
        tabs.forEach((tab: any, i: number) => {
            const columnItems = this.items.filter((el) =>
                el.hippodrome === hippodrome._id &&
                el.bettingType === Number(bettingType.id) &&
                el.bettingSubType === Number(bettingSubType.id) &&
                el.column === i + 1
            );

            if (bettingType.isCommaType) {
                // Virgüllü türlerde sıra kullanıcının verdiği sıradır, sıralama yapılmaz.
                slipItem = columnItems;
            } else {
                slipItem = _.orderBy(columnItems, [(a: any) => Number(a.horse === "s" ? -1 : a.horse)]);
            }

            legs.push(slipItem.map((el: any) => (el.horse === "s" ? "S" : el.horse)));
        });

        return legs;
    }

    async submit(save: boolean = false, imgData: any = ""): Promise<any> {
        if (this.options.inProgress || this.getCurrentItems().length === 0) {
            return null;
        }

        this.setOptions({inProgress: true});

        try {
            // Oynanamaz satır varsa burada throw edip ağa hiç çıkmıyoruz.
            const legs = this.getCouponLegs();

            const {betCount, system} = this.computeBetCount();
            const price = system
                ? Number(this.options.maxAmount)
                : Number(this.options.betSlipMultiply) * Number(betCount) * Number(this.options.bettingType.price);

            const couponData: any = {
                betType: this.options.bettingType.id,
                cardId: this.options.hippodrome.card_id,
                race: this.options.bettingSubType.label.split("-")[0],
                multiplier: this.options.betSlipMultiply,
                betCount: betCount,
                system: system,
                price: price.toFixed(2),
                legs: legs,
                poolUnit: Math.round(Number(this.options.bettingType.price) * Number(100)),
                systemAmount: 5,
            };

            return await postCoupon({
                coupon: couponData,
                save,
                usePointBalance: this.options.usePointBalance,
                shareCoupon: this.options.shareCoupon,
                complete: this.options.complete,
                couponImage: imgData,
            });
        } catch (error: any) {
            return toSubmitError(error);
        } finally {
            this.setOptions({inProgress: false});
        }
    }

    // Sistem kuponunda ayak ayak ilerleyip maxAmount'ı aşmayan en büyük at sayısını buluyor.
    private getC(ix: number, sysCols: any[]) {
        for (const c of sysCols) {
            let cR = 1;
            for (const j of _.range(Number(c.s), Number(c.h) + 1)) {
                if (ix * j * this.options.bettingType.price <= Number(this.options.maxAmount)) {
                    cR = j;
                }
            }
            ix *= cR;
        }
        return ix;
    }

    private computeBetCount(): { betCount: number; system: boolean; systemMaxMin: number[] | null } {
        if (!this.options.hippodrome || !this.options.bettingType || !this.options.bettingSubType) {
            return {betCount: 0, system: false, systemMaxMin: null};
        }

        const bettingType = this.options.bettingType;
        const slipItem = this.getCurrentItems();
        const uniqueColumn = [...Array.from(new Set(slipItem.map((i: any) => i.column)))];

        let columnCount = 1;
        let system = false;
        let systemMaxMin: number[] | null = null;
        const bankoCols: any[] = [];
        const sysCols: any[] = [];

        for (const c of uniqueColumn) {
            const cItems = slipItem.filter((i: any) => i.column === c);
            if (cItems.some((e: any) => e.horse === "s")) {
                // "s" tek bir at değil "bu ayaktaki TÜM atlar"; kolonun ağırlığı seçim sayısı
                // değil koşudaki koşan at sayısı. Koşu bulunamazsa boş diziye düşülüyor.
                const horseCount = (resolveMutualRun(cItems[0].hippodrome, cItems[0].run)?.horses ?? [])
                    .filter((h: any) => h.run_status === true).length;
                columnCount *= horseCount;
                sysCols.push({s: cItems.length, h: horseCount});
            } else {
                columnCount *= cItems.length;
                bankoCols.push({s: cItems.length});
            }
        }

        let betCount = columnCount;

        if (bettingType.column === uniqueColumn.length && sysCols.length > 0) {
            const banko = bankoCols.reduce((acc: number, c: any) => acc * c.s, 1);
            const low = this.getC(banko, sysCols);
            const high = this.getC(banko, [...sysCols].reverse());
            system = true;
            systemMaxMin = _.orderBy([low, high], [], ["asc"]);
            betCount = high;
        }

        // Plase & Ganyan: kupon hem ganyan hem plase olarak sayılıyor.
        if (bettingType.doublesBetCount) {
            betCount *= 2;
        }

        if (bettingType.isOrdered) {
            const legs = uniqueColumn.map((c) =>
                slipItem.filter((i: any) => i.column === c).map((el: any) => el.horse)
            );

            if (legs.length === 2) {
                const pairs = legs[0].flatMap((d: any) =>
                    legs[1].filter((v: any) => d !== v).map((v: any) => `${d}-${v}`)
                );
                betCount = pairs.length;
                if (bettingType.subtractsMirroredPairs) {
                    betCount -= pairs.filter((e: any) => pairs.includes(e.split("-").reverse().join("-"))).length / 2;
                }
                if (this.options.complete === true) {
                    betCount *= 2;
                }
            } else if (legs.length >= 3 && legs.length <= 5) {
                const orders: number[][] = this.options.complete === true
                    ? Array.from(new Permutation(_.range(legs.length), legs.length))
                    : [_.range(legs.length)];
                const all = new Set<string>();
                for (const order of orders) {
                    order.slice(1)
                        .reduce(
                            (acc: string[], legIndex: number) => acc.flatMap((d) =>
                                legs[legIndex]
                                    .filter((v: any) => d.split("-").indexOf(String(v)) === -1)
                                    .map((v: any) => `${d}-${v}`)
                            ),
                            legs[order[0]].map(String)
                        )
                        .forEach((e: string) => all.add(e));
                }
                betCount = all.size;
            } else {
                betCount = 0;
            }
        }

        // Virgüllü türler: sıra bahsin kendisi. Eski kod permütasyon sayısı kadar dönüp her turda
        // AYNI değeri ekliyordu - yani sonuç zaten C(n,k) × k! çarpımıydı.
        if (bettingType.isCommaType) {
            const k = bettingType.horseCount;
            const permCount = this.options.complete === true
                ? _.range(1, k + 1).reduce((a: number, n: number) => a * n, 1)
                : 1;
            betCount = Number(combination(slipItem.length, k)) * permCount;
        }

        // Tek Kolon: seçimler tek havuzda, sıra yok.
        if (bettingType.isSingleColumn) {
            betCount = Number(combination(slipItem.length, 2));
        }

        return {
            // Ayakların hepsi dolmadan kupon fiyatlanmaz; sistem bilgisi yine de döner.
            betCount: bettingType.column === uniqueColumn.length ? betCount : 0,
            system,
            systemMaxMin,
        };
    }

    // Eskiden getBetCount2 bu iki alanı render sırasında global objeye yazıyordu. Getter'ı temiz
    // tutmak için hesabı mutasyon anına çekiyoruz; değer değişmediyse hiç dokunmuyoruz (idempotans).
    private syncSystemState() {
        const {system, systemMaxMin} = this.computeBetCount();
        if (this.options.system === system && _.isEqual(this.options.systemMaxMin, systemMaxMin)) {
            return;
        }
        this.options = {...this.options, system, systemMaxMin};
    }

    private update() {
        this.syncSystemState();
        mutualBetslipSubject.next(this.items);
        // Kupon içeriği değişince sistem/hata/kolon alanları da değişmiş olabiliyor; iki akış da
        // aynı mutasyondan doğduğu için seçenekleri de tek seferde yayınlıyoruz.
        this.updateOptions();
    }

    private updateOptions() {
        mutualSlipOptionsSubject.next(this.options);
    }
}

export const MutualBetslip = new MutualBetslipService();

export function useMutualBetslip(): MutualSlipItem[] {
    return useSubjectSnapshot(mutualBetslipSubject, () => MutualBetslip.getAll());
}

export function useMutualSlipOptions(): MutualSlipOptions {
    return useSubjectSnapshot(mutualSlipOptionsSubject, () => MutualBetslip.getOptions());
}

export function restoreMutualCoupon(hippodrome: any, ticket: any): void {
    if (!hippodrome) {
        throw new Error("Bu kuponun hipodromu güncel bültende bulunamadı.");
    }

    const betTypeId = Number(ticket.bet_type);
    const raceId = Number(ticket.race_id);

    const bettingType = buildMutualBettingTypes(hippodrome).find((el: any) => Number(el.id) === betTypeId);
    if (!bettingType) {
        throw new Error("Bu kupondaki bahis türü artık bu hipodromda açık değil.");
    }

    const bettingSubType = buildMutualBettingSubTypes(bettingType).find((el: any) => Number(el.id) === raceId);
    if (!bettingSubType) {
        throw new Error(`${raceId}. koşudan başlayan bu bahis artık oynanabilir değil.`);
    }

    const runAdvancesPerLeg = String(bettingType.columnLabel) !== "At";
    const legs: any[] = Array.isArray(ticket.legs) ? ticket.legs : [];
    const rows: MutualSlipItem[] = [];

    legs.forEach((leg: any, legIndex: number) => {
        const runNo = runAdvancesPerLeg ? raceId + legIndex : raceId;
        const run = hippodrome.runsMap?.[runNo] ?? hippodrome.runs?.find((r: any) => Number(r.no) === runNo);
        if (!run) {
            throw new Error(`${runNo}. koşu güncel bültende bulunamadı.`);
        }

        (Array.isArray(leg) ? leg : [leg]).forEach((horse: any) => {
            // 32, "bu ayaktaki tüm atlar" (S) için sunucunun kullandığı özel kod.
            if (Number(horse) === 32) {
                rows.push({
                    hippodrome: hippodrome._id,
                    bettingType: betTypeId,
                    bettingSubType: raceId,
                    column: legIndex + 1,
                    run: runNo,
                    horse: "s",
                });
                return;
            }

            // T2/T3/T4 sabit ihtimalli bahis ekleri; müşterekte çıkmaması gerekir ama eski
            // kayıtlarda görülebildiği için savunma amaçlı temizleniyor.
            const horseNo = String(horse).replaceAll("T2", "").replaceAll("T3", "").replaceAll("T4", "");
            const horseObject = run.horseMap?.[horseNo] ?? run.horses?.find((h: any) => String(h.no) === horseNo);
            if (!horseObject || horseObject.run_status !== true) {
                throw new Error(`${runNo}. koşudaki ${horseNo} numaralı at artık koşmuyor.`);
            }

            rows.push({
                hippodrome: hippodrome._id,
                bettingType: betTypeId,
                bettingSubType: raceId,
                column: legIndex + 1,
                run: runNo,
                horse: horseNo,
            });
        });
    });

    if (rows.length === 0) {
        throw new Error("Kuponda yüklenebilecek bir seçim bulunamadı.");
    }

    MutualBetslip.setOptions({
        hippodrome: hippodrome,
        bettingType: bettingType,
        bettingSubType: bettingSubType,
        column: 1,
        betSlipMultiply: Number(ticket.multiply) > 0 ? Number(ticket.multiply) : 1,
        complete: false,
        usePointBalance: false,
        shareCoupon: false,
        errorMessage: null,
    });
    MutualBetslip.restoreItems(rows);
}

// Yeniden oynatma başarısız olduğunda kullanıcıya gösterilen tek mesaj noktası. Kupon KISALTILMIŞ
// hâlde asla açılmaz; ya tamamı yüklenir ya da bu uyarı çıkar.
export function showMutualReplayError(message: string) {
    modalServiceSubject.next({
        title: "Kupon Yüklenemedi",
        content: `<div class="text-center"><p>${message}</p><p>Bu kupon artık aynen oynanamıyor. Dilerseniz seçimlerinizi kendiniz yapabilir ya da başka bir kupon deneyebilirsiniz.</p></div>`,
        confirm: {
            cancel: {
                label: "Tamam",
            },
        },
    });
}
